import type { BlockType } from "@babackapp/blocks-editor/types";
import { motion } from "motion/react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ReactNode, useState } from "react";
import { AddBlockIcon } from "./add-block-icon";
import { LucideProps } from "lucide-react";

export interface BlockDragData {
  blockType: BlockType;
  title: string;
  description: string;
  icon: React.FC<LucideProps>;
}

interface EditorDndContextProps {
  children: ReactNode;
}

export function EditorDndContext({ children }: EditorDndContextProps) {
  const [activeBlock, setActiveBlock] = useState<BlockDragData | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current) {
      setActiveBlock(active.data.current as BlockDragData);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.data.current && over.data.current) {
      const blockData = active.data.current as BlockDragData;
      const dropData = over.data.current as {
        onBlockDrop: (
          blockType: BlockType,
          section: string,
          index: number,
        ) => void;
        section: string;
        index: number;
      };

      if (dropData.onBlockDrop) {
        dropData.onBlockDrop(
          blockData.blockType,
          dropData.section,
          dropData.index,
        );
      }
    }

    setActiveBlock(null);
  };

  const handleDragCancel = () => {
    setActiveBlock(null);
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {activeBlock && (
          <motion.div
            className="rounded-lg bg-card border cursor-grabbing"
            initial={{
              scale: 1,
              rotate: 0,
              boxShadow: "0 0 0px rgba(0, 0, 0, 0.2)",
            }}
            animate={{
              scale: 0.85,
              rotate: 6,
              boxShadow: "0 0 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <AddBlockIcon
              title={activeBlock.title}
              description={activeBlock.description}
              icon={activeBlock.icon}
            />
          </motion.div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
